import React, { useCallback, useState } from 'react'
import { AlertTriangle, CheckCircle2, RefreshCw } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import { Sidebar } from '@/components/ui/sidebar'
import Header from '@/components/ui/header'
import BottomNav from '@/components/ui/bottom-nav'
import { toast } from '@/components/ui/use-toast'
import {
  useDashboard,
  ActivityPanel,
  DashboardSkeleton,
  SuggestionCards,
  ThisWeekPanel,
  TodayPanel,
  ZephBrief,
} from '@/features/dashboard'
import { useGlobalSearch, SearchResultsDropdown } from '@/features/global-search'

// The screen reads top to bottom as a narrative — what happened (the brief), what's
// possible (suggestions), what needs you (Today) — rather than a grid of co-equal
// boxes. The layout is fixed on purpose: a landing surface that rearranges itself is
// harder to learn, not more flexible.
export const DashboardPage: React.FC = () => {
  const { currentTheme } = useTheme()
  const [search, setSearch] = useState('')
  const { snapshot, role, workspaceId, loading, error, refresh, refreshing, regenerateBrief, regenerating, resolveApproval } =
    useDashboard()
  const { groups, loading: searchLoading, hasQuery } = useGlobalSearch(search, workspaceId, snapshot?.hubspot)

  // Manual refresh only — the background stream/safety-poll updates silently, a
  // toast per tick would be noise. `refresh()` treats hubspot.connected (not just
  // the request's own 200) as the real success signal — see useDashboard.ts.
  const handleRefresh = useCallback(async () => {
    const result = await refresh()
    if (result.ok) {
      toast({
        variant: 'success',
        description: (
          <span className="flex items-center gap-2">
            <CheckCircle2 size={16} /> Data synced
          </span>
        ),
      })
    } else {
      toast({ variant: 'destructive', title: 'Sync failed', description: result.reason })
    }
  }, [refresh])

  const handleRegenerate = useCallback(async () => {
    const result = await regenerateBrief()
    if (!result.ok) {
      toast({ variant: 'destructive', title: "Couldn't rewrite the brief", description: result.reason })
    }
  }, [regenerateBrief])

  const handleResolveApproval = useCallback(
    async (conversationId: string, action: 'approved' | 'rejected') => {
      const result = await resolveApproval(conversationId, action)
      if (result.ok) {
        toast({
          variant: 'success',
          description: (
            <span className="flex items-center gap-2">
              <CheckCircle2 size={16} /> {action === 'approved' ? 'Approved — Zeph is running it now' : 'Rejected'}
            </span>
          ),
        })
      } else {
        toast({ variant: 'destructive', title: "Couldn't submit your decision", description: result.reason })
      }
      return result
    },
    [resolveApproval],
  )

  // Activity is owner/admin only. The backend enforces the same rule (it returns
  // `{ status: 'restricted' }` and withholds the data), so this only avoids
  // rendering an empty shell — and it fails closed while `role` is unresolved.
  const canSeeActivity = role === 'owner' || role === 'admin'

  return (
    <div
      className="h-full flex flex-col overflow-hidden transition-colors duration-300"
      style={{ backgroundColor: currentTheme.primary }}
    >
      <div className="flex flex-1 min-h-0 overflow-hidden pb-16 md:pb-0">
        <Sidebar />

        <div className="flex-1 flex flex-col overflow-hidden">
          <Header
            search
            searchGrow
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search…"
            searchDropdown={
              <SearchResultsDropdown
                groups={groups}
                loading={searchLoading}
                hasQuery={hasQuery}
                query={search}
                onAfterSelect={() => setSearch('')}
              />
            }
            actions={
              <button
                type="button"
                onClick={() => void handleRefresh()}
                disabled={refreshing || loading}
                aria-label="Resync"
                title="Resync data from HubSpot"
                className="inline-flex items-center gap-1.5 h-[34px] px-2 sm:px-3 rounded-lg text-xs font-medium transition-all duration-300 hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: currentTheme.secondary,
                  color: currentTheme.text,
                  border: `1px solid ${currentTheme.border}`,
                }}
              >
                <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
                <span className="hidden sm:inline">Resync</span>
              </button>
            }
          />

          <main className="flex-1 min-h-0 overflow-y-auto custom-scrollbar p-4 md:p-6">
            <div className="max-w-[1140px] mx-auto">
              {loading ? (
                <DashboardSkeleton />
              ) : error ? (
                <div className="flex flex-col items-center justify-center py-24 gap-4 text-center">
                  <div
                    className="flex h-14 w-14 items-center justify-center rounded-xl"
                    style={{ backgroundColor: currentTheme.errorBackground }}
                  >
                    <AlertTriangle size={24} style={{ color: currentTheme.errorText }} />
                  </div>
                  <div>
                    <p className="font-semibold text-base" style={{ color: currentTheme.text }}>
                      Couldn&apos;t load your dashboard
                    </p>
                    <p className="text-sm mt-1" style={{ color: currentTheme.mutedText }}>
                      {error}
                    </p>
                  </div>
                </div>
              ) : snapshot ? (
                <div className="flex flex-col gap-4">
                  <ZephBrief
                    panel={snapshot.brief}
                    onRegenerate={() => void handleRegenerate()}
                    regenerating={regenerating}
                  />

                  <SuggestionCards panel={snapshot.suggestions} />

                  <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] gap-4 items-start">
                    <TodayPanel
                      panel={snapshot.today}
                      hubspot={snapshot.hubspot}
                      onResolveApproval={handleResolveApproval}
                    />

                    <div className="flex flex-col gap-4 min-w-0">
                      <ThisWeekPanel panel={snapshot.thisWeek} />
                      {canSeeActivity && <ActivityPanel panel={snapshot.activity} />}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </main>
        </div>
      </div>

      <BottomNav />
    </div>
  )
}

export default DashboardPage
